
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-picker-with-range';
import { Checkbox } from '@/components/ui/checkbox';
import { Download } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { exportTypes, availableTables, formats } from './types/export';

interface ExportConfigurationFormProps {
  onExport: (config: {
    exportType: string;
    dateRange: DateRange | undefined;
    selectedTables: string[];
    format: string;
  }) => void;
  isLoading: boolean;
}

const ExportConfigurationForm: React.FC<ExportConfigurationFormProps> = ({
  onExport,
  isLoading
}) => {
  const [exportType, setExportType] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<string>('csv');

  const handleTableToggle = (table: string) => {
    setSelectedTables(prev => 
      prev.includes(table) 
        ? prev.filter(t => t !== table)
        : [...prev, table]
    );
  };

  const handleSubmit = () => {
    onExport({
      exportType,
      dateRange,
      selectedTables: exportType === 'comprehensive' ? selectedTables : [],
      format: exportFormat
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Data Export Manager
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium mb-2 block">Export Type</label>
            <Select value={exportType} onValueChange={setExportType}>
              <SelectTrigger>
                <SelectValue placeholder="Select export type" />
              </SelectTrigger>
              <SelectContent>
                {exportTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Format</label>
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {formats.map((fmt) => (
                  <SelectItem key={fmt.value} value={fmt.value}>
                    {fmt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Date Range</label>
          <DatePickerWithRange
            date={dateRange}
            onDateChange={setDateRange}
          />
        </div>

        {exportType === 'comprehensive' && (
          <div>
            <label className="text-sm font-medium mb-2 block">Select Tables</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {availableTables.map((table) => (
                <div key={table} className="flex items-center space-x-2">
                  <Checkbox
                    id={table}
                    checked={selectedTables.includes(table)}
                    onCheckedChange={() => handleTableToggle(table)}
                  />
                  <label htmlFor={table} className="text-sm">
                    {table}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button 
          onClick={handleSubmit} 
          disabled={isLoading || !exportType}
          className="w-full"
        >
          {isLoading ? 'Creating Export...' : 'Create Export'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ExportConfigurationForm;
